import axios from 'axios';
import { apiClient } from './apiClient';
import { apiConfig } from '@/config/apiConfig';
import { handleServiceError } from '@/utils/errorHandler';

export interface District {
  id: string;
  name: string;
  state?: string;
}

export interface Subdistrict {
  id: string;
  subdistrict_name: string;
  district_name?: string;
}

export interface Village {
  village_id: string;
  village_name: string;
  subdistrict_name?: string;
  district_name?: string;
}

export interface LocationResponse {
  districts: District[];
}

export interface SubdistrictResponse {
  data: {
    list: Subdistrict[];
  };
}

export interface VillageResponse {
  data: {
    list: Village[];
  };
}

export class LocationService {
  /**
   * Get list of districts for location selection
   */
  async getDistricts(): Promise<District[]> {
    try {
      const url = '/console/asset_management/location/';
      
      const response = await apiClient.get(url);
      
      // Handle different possible response formats
      let districts: any[] = [];
      
      if (Array.isArray(response.data)) {
        // Response is directly an array (confirmed format)
        districts = response.data;
      } else if (response.data?.districts && Array.isArray(response.data.districts)) {
        // Response has districts property
        districts = response.data.districts;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        // Response has data property
        districts = response.data.data;
      } else if (response.data?.locations && Array.isArray(response.data.locations)) {
        // Response has locations property
        districts = response.data.locations;
      } else {
        districts = [];
      }
      
      return districts;
    } catch (error: any) {
      console.error('Error fetching districts:', error);
      handleServiceError(error, 'Fetch Districts');
    }
  }

  /**
   * Format districts for form select options
   */
  async getDistrictOptions(): Promise<Array<{ value: string; label: string }>> {
    try {
      const districts = await this.getDistricts();
      
      const formatted = districts.map((district: any, index: number) => {
        
        // Handle string format (confirmed API response format)
        if (typeof district === 'string') {
          return { 
            value: district, 
            label: district 
          };
        }
        
        // Handle object format (fallback)
        const value = district.id || district.value || district.name || district.district_name || district.code || `district-${index}`;
        const label = district.name || district.label || district.district_name || district.title || district.display_name || district.id || `District ${index + 1}`;
        
        return { value, label };
      });
      
      return formatted;
    } catch (error) {
      // Provide fallback data on error using actual API district names
      return [
        { value: 'Amritsar', label: 'Amritsar' },
        { value: 'Hoshiarpur', label: 'Hoshiarpur' },
        { value: 'Jalandhar', label: 'Jalandhar' },
        { value: 'Ludhiana', label: 'Ludhiana' },
        { value: 'Patiala', label: 'Patiala' },
      ];
    }
  }

  /**
   * Get list of subdistricts for a district
   */
  async getSubdistricts(districtName: string): Promise<SubdistrictResponse> {
    try {
      const url = `/console/asset_management/location/${encodeURIComponent(districtName)}`;
      
      const response = await apiClient.get(url);
      
      // Handle different possible response formats
      let subdistricts: any[] = [];
      
      if (response.data?.data?.list) {
        // Expected format with nested structure
        subdistricts = response.data.data.list;
      } else if (Array.isArray(response.data)) {
        // API returns direct array of strings (actual format)
        subdistricts = response.data.map((name: string) => ({
          id: name.toLowerCase().replace(/\s+/g, '_'),
          subdistrict_name: name,
          district_name: districtName
        }));
      } else {
        // Fallback to mock data if API doesn't return expected format
        subdistricts = [
          { id: 'sub_1', subdistrict_name: `${districtName} Sub-1`, district_name: districtName },
          { id: 'sub_2', subdistrict_name: `${districtName} Sub-2`, district_name: districtName },
          { id: 'sub_3', subdistrict_name: `${districtName} Sub-3`, district_name: districtName },
        ];
      }
      
      return {
        data: {
          list: subdistricts
        }
      };
    } catch (error: any) {
      console.error('Error fetching subdistricts for district:', districtName, error);
      handleServiceError(error, 'Fetch Subdistricts');
    }
  }

  /**
   * Get list of villages for a subdistrict
   */
  async getVillages(districtName: string, subdistrictName: string): Promise<VillageResponse> {
    try {
      const url = `/console/asset_management/location/${encodeURIComponent(districtName)}/${encodeURIComponent(subdistrictName)}`;
      
      const response = await apiClient.get(url);
      
      // Handle different possible response formats
      let villages: any[] = [];
      
      if (response.data?.data?.list) {
        // Expected format with nested structure
        villages = response.data.data.list;
      } else if (Array.isArray(response.data)) {
        // API returns direct array of objects (actual format)
        villages = response.data.map((item: any, index: number) => {
          if (typeof item === 'string') {
            // If API returns array of strings
            return {
              village_id: `V_${index + 1001}`,
              village_name: item,
              subdistrict_name: subdistrictName,
              district_name: districtName
            };
          } else if (item && typeof item === 'object') {
            // Handle actual API format: { location_name_in_english: "...", id: "V_40034", pincode: 148104 }
            return {
              village_id: item.id || item.village_id || `V_${index + 1001}`,
              village_name: item.location_name_in_english || item.village_name || item.name || item.title || `Village ${index + 1}`,
              subdistrict_name: item.subdistrict_name || subdistrictName,
              district_name: item.district_name || districtName
            };
          }
          return item;
        });
      } else {
        // Fallback to mock data if API doesn't return expected format
        villages = [
          { village_id: 'V_1001', village_name: `${subdistrictName} Village 1`, subdistrict_name: subdistrictName, district_name: districtName },
          { village_id: 'V_1002', village_name: `${subdistrictName} Village 2`, subdistrict_name: subdistrictName, district_name: districtName },
          { village_id: 'V_1003', village_name: `${subdistrictName} Village 3`, subdistrict_name: subdistrictName, district_name: districtName },
        ];
      }
      
      return {
        data: {
          list: villages
        }
      };
    } catch (error: any) {
      console.error('Error fetching villages for district/subdistrict:', districtName, subdistrictName, error);
      handleServiceError(error, 'Fetch Villages');
    }
  }
}

// Export singleton instance
export const locationService = new LocationService();
export default locationService;

// ─── Tracker API Types (Supabase Edge Functions) ──────────────────────────────

export interface CheckinLocation {
  longitude: number | null;
  latitude: number | null;
}

export interface ActivitySession {
  checkin_time: string | null;
  checkout_time: string | null;
  checkin_location: CheckinLocation;
  checkout_location: CheckinLocation;
}

export interface UserActivity {
  user_id: string;
  name: string | null;
  role: string[];
  sessions: ActivitySession[];
}

export interface PageInfo {
  totalRecords: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface UserActivityResponse {
  data: UserActivity[];
  pageInfo: PageInfo;
}

export interface UserActivityParams {
  date?: string;
  program?: string;
  page_number?: number;
  page_size?: number;
}

// Coordinates are GeoJSON order: [longitude, latitude]
export interface MovementSegment {
  start_time: string;
  end_time: string;
  number_of_points: number;
  total_distance_meters: number;
  coordinates: [number, number][];
}

export interface TrackingTask {
  title: string;
  description: string;
}

export interface TrackingSession {
  checkin_id: string;
  checkin_time: string;
  checkout_time: string | null;
  movement_segments: MovementSegment[];
  tasks: TrackingTask[];
  remarks: string[];
}

export interface TrackingUser {
  user_id: string;
  sessions: TrackingSession[];
}

export interface UserListItem {
  user_id: string;
  fusion_auth_user_id: string;
  name: string;
  image: string | null;
  employee_id: string;
  program_id: string;
  districts: string[];
}

// ─── Tracker Service ──────────────────────────────────────────────────────────

export class TrackerService {
  private getAuthToken(): string | null {
    return (
      localStorage.getItem(apiConfig.auth.tokenKey) ||
      sessionStorage.getItem(apiConfig.auth.tokenKey)
    );
  }

  private async get<T>(path: string, params?: Record<string, any>): Promise<T> {
    const token = this.getAuthToken();
    const response = await axios.get<T>(
      `${apiConfig.trackerBaseURL}${path}`,
      {
        params,
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        timeout: apiConfig.timeout,
      }
    );
    return response.data;
  }

  async getUserActivity(params: UserActivityParams = {}): Promise<UserActivityResponse> {
    try {
      return await this.get<UserActivityResponse>(
        apiConfig.endpoints.tracker.userActivity,
        params
      );
    } catch (error) {
      handleServiceError(error, 'Fetch User Activity');
      throw error;
    }
  }

  async getTracking(userId: string, date?: string): Promise<TrackingUser[]> {
    try {
      return await this.get<TrackingUser[]>(
        apiConfig.endpoints.tracker.tracking,
        { user_id: userId, ...(date ? { date } : {}) }
      );
    } catch (error) {
      handleServiceError(error, 'Fetch Tracking');
      throw error;
    }
  }

  async getUserList(program: string): Promise<UserListItem[]> {
    try {
      return await this.get<UserListItem[]>(
        apiConfig.endpoints.tracker.userList,
        { program }
      );
    } catch (error) {
      handleServiceError(error, 'Fetch User List');
      throw error;
    }
  }
}

export const trackerService = new TrackerService();